
/**
 * Hook: useManualAssets
 *
 * Manages user-created (manual) investment assets with React Query caching.
 * Delegates persistence to `ManualAssetsService` and performs allocation
 * checks/cascades via `PortfolioAllocationsService`.
 *
 * Returns fetched assets, loading/error state, computed totals, and
 * CRUD helpers (`createManualAsset`, `updateManualAsset`, `deleteManualAsset`),
 * plus `checkPortfolioAllocations` utilities for safe deletions.
 */

import { useCallback } from 'react';
import { useAuthStore } from '@/stores/authStore';
import { logger } from '@/services/LoggingService';
import { toast } from 'sonner';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { fetchManualAssetsForUser, createManualAsset as createManualAssetSvc, updateManualAsset as updateManualAssetSvc, deleteManualAsset as deleteManualAssetSvc } from '@/services/manual/ManualAssetsService';
import { findUserPortfolioNamesForAsset, findUserPortfolioNamesForAssets, deleteAllocationsByAssetId } from '@/services/portfolio/PortfolioAllocationsService';

// Define types that match our database schema but are isolated to this provider
export interface ManualAsset {
  id: string;
  user_id: string;
  account_id: string | null; // Updated: manual assets now normalize account_id to NULL
  asset_name: string;
  symbol: string | null;
  asset_type: 'cash' | 'equity' | 'fixed_income' | 'alternative' | 'multi_asset' | 'holding';
  quantity: number | null;
  market_value: number;
  book_value: number | null;
  currency: string;
  is_manual: boolean;
  provider_name: string;
  created_at: string;
  updated_at: string | null;
  // New fields
  registered: 'registered' | 'non-registered';
  description: string | null;
  type: string | null;
  asset_url: string | null;
}

export interface ManualAssetInput {
  asset_name: string;
  symbol?: string;
  asset_type: 'cash' | 'equity' | 'fixed_income' | 'alternative' | 'multi_asset' | 'holding';
  quantity?: number;
  market_value: number;
  book_value?: number;
  currency: string;
  // New optional fields
  registered?: 'registered' | 'non-registered';
  description?: string;
  type?: string;
  asset_url?: string;
}

export function useManualAssets() {
  const queryClient = useQueryClient();
  const { user } = useAuthStore();
  
  // Define query key for manual assets
  const MANUAL_ASSETS_QUERY_KEY = ['manualAssets', user?.id];
  
  // Fetch all manual assets for the current user using React Query
  const fetchManualAssets = useCallback(async () => {
    if (!user) return [];
    
    try {
      // Fetch all manual assets for this user via service
      const manualAssets = await fetchManualAssetsForUser(user.id);
      
      // Sort by created_at (most recent first)
      manualAssets.sort((a: any, b: any) => 
        new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      
      logger.info('useManualAssets.fetchManualAssets', 'Successfully fetched manual assets', {
        count: manualAssets?.length || 0
      });

      return manualAssets as ManualAsset[];
    } catch (err: any) {
      logger.error('useManualAssets.fetchManualAssets', 'Failed to fetch manual assets', {
        error: err.message
      });
      throw err;
    }
  }, [user]);
  
  // Use React Query to fetch and cache manual assets
  const { data: assets = [], isLoading, error } = useQuery({
    queryKey: MANUAL_ASSETS_QUERY_KEY,
    queryFn: fetchManualAssets,
    enabled: !!user,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true
  });

  // Create a new manual asset using React Query mutation
  const createAssetMutation = useMutation({
    mutationFn: async (assetData: ManualAssetInput): Promise<ManualAsset> => {
      if (!user) { toast.error('You must be logged in to add assets'); throw new Error('User not authenticated'); }

      const dbAsset = {
        account_id: null,
        asset_name: assetData.asset_name,
        symbol: assetData.symbol || null,
        asset_type: assetData.asset_type,
        quantity: assetData.quantity || null,
        market_value: assetData.market_value,
        book_value: assetData.book_value || null,
        currency: assetData.currency,
        is_manual: true,
        provider_name: 'manual',
        created_at: new Date().toISOString(),
      };

      const created = await createManualAssetSvc(user.id, dbAsset);
      logger.info('useManualAssets.createManualAsset', 'Asset added successfully', { asset_id: created?.id, asset_name: assetData.asset_name });
      return created as ManualAsset;
    },
    onSuccess: () => {
      // Invalidate and refetch manual assets query
      queryClient.invalidateQueries({queryKey: MANUAL_ASSETS_QUERY_KEY});
      // Also invalidate unified assets so lists reflect changes immediately
      queryClient.invalidateQueries({ queryKey: ['assets-unified'] });
      // Note: Success toast is handled by the form component to avoid duplicates
    },
    onError: (error: Error) => {
      logger.error('useManualAssets.createManualAsset', 'Failed to add asset', {
        error: error?.message || 'Unknown error',
      });
      toast.error(`Failed to add asset: ${error.message}`);
    }
  });

  // Wrapper function to maintain API compatibility
  const createManualAsset = async (assetData: ManualAssetInput): Promise<boolean> => {
    try {
      await createAssetMutation.mutateAsync(assetData);
      return true;
    } catch (error) {
      return false;
    }
  };

  // Update an existing manual asset using React Query mutation
  const updateAssetMutation = useMutation({
    mutationFn: async ({ id, assetData }: { id: string; assetData: Partial<ManualAssetInput> }): Promise<void> => {
      if (!user) { toast.error('You must be logged in to update assets'); throw new Error('User not authenticated'); }
      const updateData = { ...assetData, updated_at: new Date().toISOString() };
      await updateManualAssetSvc(user.id, id, updateData);
      logger.info('useManualAssets.updateManualAsset', 'Asset updated successfully', { asset_id: id });
    },
    onSuccess: () => {
      // Invalidate and refetch manual assets query
      queryClient.invalidateQueries({queryKey: MANUAL_ASSETS_QUERY_KEY});
      // Also invalidate unified assets so lists reflect changes immediately
      queryClient.invalidateQueries({ queryKey: ['assets-unified'] });
      // Note: Success toast is handled by the UI component to avoid duplicates
    },
    onError: (error: Error) => {
      logger.error('useManualAssets.updateManualAsset', 'Failed to update asset', {
        error: error?.message || 'Unknown error',
      });
      toast.error(`Failed to update asset: ${error.message}`);
    }
  });

  // Wrapper function to maintain API compatibility
  const updateManualAsset = async (id: string, assetData: Partial<ManualAssetInput>): Promise<boolean> => {
    try {
      await updateAssetMutation.mutateAsync({ id, assetData });
      return true;
    } catch (error) {
      return false;
    }
  };

  // Check for portfolio allocations and get portfolio names
  const checkPortfolioAllocations = async (assetId: string): Promise<{ count: number; portfolioNames: string[] }> => {
    if (!user?.id) return { count: 0, portfolioNames: [] };
    const { count, names } = await findUserPortfolioNamesForAsset(assetId, user.id);
    return { count, portfolioNames: names };
  };

  // Bulk check for portfolio allocations for multiple asset IDs
  const checkPortfolioAllocationsBulk = async (
    assetIds: string[]
  ): Promise<Record<string, { count: number; portfolioNames: string[] }>> => {
    if (!assetIds || assetIds.length === 0) return {};

    if (!user?.id) return {};
    const m = await findUserPortfolioNamesForAssets(assetIds, user.id);
    const result: Record<string, { count: number; portfolioNames: string[] }> = {};
    Object.entries(m).forEach(([aid, v]) => { result[aid] = { count: v.count, portfolioNames: v.names }; });
    return result;
  };

  // Delete a manual asset using React Query mutation
  const deleteAssetMutation = useMutation({
    mutationFn: async ({ id, forceDelete = false }: { id: string; forceDelete?: boolean }): Promise<void> => {
      if (!user) {
        toast.error('You must be logged in to delete assets');
        throw new Error('User not authenticated');
      }

      // Check for portfolio allocations unless force delete
      if (!forceDelete) {
        const allocationInfo = await checkPortfolioAllocations(id);
        if (allocationInfo.count > 0) {
          const portfolioList = allocationInfo.portfolioNames.join(', ');
          throw new Error(`PORTFOLIO_ALLOCATIONS_EXIST:${allocationInfo.count}:${portfolioList}`);
        }
      }

      // If user confirmed cascade delete, remove portfolio allocations first (for user's portfolios)
      if (forceDelete) { await deleteAllocationsByAssetId(id); }

      // Delete from investment_assets
      await deleteManualAssetSvc(user.id, id);
      logger.info('useManualAssets.deleteManualAsset', 'Asset deleted successfully', { asset_id: id });
    },
    onSuccess: () => {
      // Invalidate and refetch manual assets query
      queryClient.invalidateQueries({queryKey: MANUAL_ASSETS_QUERY_KEY});
      // Also invalidate unified assets so lists reflect changes immediately
      queryClient.invalidateQueries({ queryKey: ['assets-unified'] });
      // Note: Success toast removed to avoid unwanted notifications when deletion is handled by dialog
    },
    onError: (error: Error) => {
      // Don't log portfolio allocation checks as errors - they're expected business logic
      if (error.message && error.message.startsWith('PORTFOLIO_ALLOCATIONS_EXIST:')) {
        logger.info('useManualAssets.deleteManualAsset', 'Asset deletion blocked due to portfolio allocations', {
          allocation_info: error.message,
        });
        // Portfolio allocation errors are handled by the dialog, don't show toast
        return;
      }
      
      // Only log actual errors
      logger.error('useManualAssets.deleteManualAsset', 'Failed to delete asset', {
        error: error?.message || 'Unknown error',
      });
      
      toast.error(`Failed to delete asset: ${error.message}`);
    }
  });

  // Wrapper function to maintain API compatibility
  const deleteManualAsset = async (id: string, forceDelete = false): Promise<boolean> => {
    try {
      await deleteAssetMutation.mutateAsync({ id, forceDelete });
      return true;
    } catch (error) {
      // Re-throw portfolio allocation errors so they can be handled by the UI
      if (error instanceof Error && error.message.startsWith('PORTFOLIO_ALLOCATIONS_EXIST:')) {
        throw error;
      }
      return false;
    }
  };

  // Calculate total manual asset value
  const totalValue = assets.reduce((sum, asset) => sum + asset.market_value, 0);

  return {
    assets,
    isLoading,
    error,
    totalValue,
    fetchManualAssets,
    checkPortfolioAllocations,
    checkPortfolioAllocationsBulk,
    createManualAsset,
    updateManualAsset,
    deleteManualAsset,
  };
}
